import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { EditorCkMain } from "../../components/CkEditor";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SaveIcon from "@material-ui/icons/Save";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const PRIORITY_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);

export const UpdateService = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [servPic, setServPic] = useState({ raw: "", preview: "" });
  const [oldImage, setOldImage] = useState("");
  const [priority, setPriority] = useState("15");
  const [status, setStatus] = useState(true);
  const [title, setTitle] = useState("");
  const [keywords, setKeywords] = useState("");
  const [metadesc, setMetaDesc] = useState("");

  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const formData = new FormData();
    formData.append("id", id);
    Axios.post("/api/getsingleservice", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        const d = res.data.data;
        setName(d.serv_name || "");
        setContent(d.serv_desc || "");
        if (d.serv_image) setOldImage(d.serv_image);
        setPriority(String(d.serv_priority || 15));
        setTitle(d.serv_title || "");
        setKeywords(d.serv_keywords || "");
        setMetaDesc(d.serv_metadesc || "");
        setStatus(d.serv_status === 1);
        setIsLoading(false);
      } else {
        toast.error("Could not load service");
        navigate("/dash/allservices");
      }
    });
  }, [id, navigate, toast]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setServPic({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!name.trim()) {
      setErr(1);
      toast.error("Please enter a service name.");
      return;
    }
    if (!content || !content.trim()) {
      setErr(2);
      toast.error("Please enter the service description.");
      return;
    }
    if (!servPic.raw && !oldImage) {
      setErr(3);
      toast.error("Please select a service image.");
      return;
    }
    if (!title.trim()) {
      setErr(4);
      toast.error("Please enter the meta title.");
      return;
    }
    if (!keywords.trim()) {
      setErr(5);
      toast.error("Please enter meta keywords.");
      return;
    }
    if (!metadesc.trim()) {
      setErr(6);
      toast.error("Please enter the meta description.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (servPic.raw) formData.append("image", servPic.raw);
    formData.append("idd", id);
    formData.append("name", name.trim());
    formData.append("desc", content);
    formData.append("servpriority", priority);
    formData.append("title", title.trim());
    formData.append("keywords", keywords.trim());
    formData.append("metadesc", metadesc.trim());

    Axios.post("/api/updateservice", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Service updated");
          navigate("/dash/allservices");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Service - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Services"
        title="Edit service"
        subtitle="Update the service details, image, and SEO metadata."
        backTo="/dash/allservices"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Service name"
            variant="outlined"
            error={err === 1}
            value={name}
            size="small"
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            variant="outlined"
            value={priority}
            size="small"
            onChange={(e) => setPriority(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
            helperText="1 = highest"
          >
            {PRIORITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Description</Typography>
          <EditorCkMain desc={content} setDescr={(e) => setContent(e)} />
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Service image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="service-image-upload"
              type="file"
            />
            <label htmlFor="service-image-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  servPic.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {servPic.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {servPic.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{servPic.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>
                          {oldImage ? "Replace service image" : "Upload service image"}
                        </strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 5 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            <Box style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {oldImage && !servPic.preview && (
                <Box>
                  <Typography className={classes.uploadHint} style={{ marginBottom: 4 }}>
                    Current image
                  </Typography>
                  <img
                    src={"/static/services/" + oldImage}
                    alt="current"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 200, maxHeight: 160 }}
                  />
                </Box>
              )}
              {servPic.preview && (
                <Box>
                  <Typography className={classes.uploadHint} style={{ marginBottom: 4 }}>
                    New image preview
                  </Typography>
                  <img
                    src={servPic.preview}
                    alt="preview"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 200, maxHeight: 160 }}
                  />
                </Box>
              )}
            </Box>
            {err === 3 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>SEO meta details</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Meta title"
            variant="outlined"
            error={err === 4}
            value={title}
            size="small"
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Meta keywords"
            variant="outlined"
            error={err === 5}
            value={keywords}
            size="small"
            multiline
            rows={3}
            onChange={(e) => setKeywords(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Meta description"
            variant="outlined"
            error={err === 6}
            value={metadesc}
            size="small"
            multiline
            rows={3}
            onChange={(e) => setMetaDesc(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/allservices")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Updating..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
